import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Camera, ExternalLink, FileText, Video } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { evidenceIsImage, evidenceIsVideo, normalizeEvidenceItems } from '../../lib/evidenceUtils';
import { getApiErrorMessage } from '../../lib/apiError';

const apiPath = (url) => (url?.startsWith('/api') ? url.replace(/^\/api/, '') : url);

const EvidencePreview = ({ reference, onOpen, opening }) => {
  const { authApi } = useAuth();
  const [previewUrl, setPreviewUrl] = useState('');
  const url = reference?.fileUrl || reference?.url;
  const isImage = evidenceIsImage(reference);
  const isVideo = evidenceIsVideo(reference);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = '';

    const loadPreview = async () => {
      if (!url || (!isImage && !isVideo)) return;
      if (!url.startsWith('/api') && !url.includes('/api/')) {
        setPreviewUrl(url);
        return;
      }
      try {
        const response = await authApi.get(apiPath(url), { responseType: 'blob' });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(response.data);
        setPreviewUrl(objectUrl);
      } catch {
        if (!cancelled) setPreviewUrl('');
      }
    };

    loadPreview();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [authApi, isImage, isVideo, url]);

  return (
    <button
      type="button"
      onClick={() => onOpen(reference)}
      disabled={opening || !url}
      className="group relative h-28 overflow-hidden rounded-xl border border-[#E8E6E3] bg-[#F7F4EE] text-left disabled:opacity-60"
    >
      {previewUrl && isImage ? (
        <img src={previewUrl} alt={reference?.fileName || 'Evidence'} className="h-full w-full object-cover" />
      ) : previewUrl && isVideo ? (
        <video src={previewUrl} className="h-full w-full bg-black object-cover" muted playsInline />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-[#9F1D35]">
          {isVideo ? <Video className="h-7 w-7" /> : <FileText className="h-7 w-7" />}
        </div>
      )}
      <span className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-[#1A1A1A]/70 px-2 py-1 text-xs font-medium text-[#FAFAF7]">
        <span className="truncate">{reference?.fileName || reference?.evidenceType || 'Evidence'}</span>
        <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
      </span>
    </button>
  );
};

const EvidenceButton = ({ label, reference, items, compact = false }) => {
  const { authApi } = useAuth();
  const [openingId, setOpeningId] = useState('');
  const evidenceItems = useMemo(() => normalizeEvidenceItems(reference, items), [reference, items]);

  const openEvidence = async (item) => {
    const url = item?.fileUrl || item?.url;
    if (!url) return;
    if (!url.startsWith('/api') && !url.includes('/api/')) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    setOpeningId(item.id || url);
    try {
      const response = await authApi.get(apiPath(url), { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(response.data);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to open evidence file.'));
    } finally {
      setOpeningId('');
    }
  };

  return (
    <div className={`rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] ${compact ? 'p-3' : 'p-4'}`}>
      <p className="mb-3 flex items-center gap-2 text-sm font-medium text-[#1A1A1A]">
        <Camera className="h-4 w-4 text-[#9F1D35]" />
        {label}
        {evidenceItems.length > 1 && (
          <span className="rounded-full bg-[#F5E8EA] px-2 py-0.5 text-xs text-[#9F1D35]">{evidenceItems.length}</span>
        )}
      </p>
      {evidenceItems.length > 0 ? (
        <div className={`grid gap-2 ${compact ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3'}`}>
          {evidenceItems.map((item, index) => (
            <EvidencePreview
              key={item.id || `${label}-${index}`}
              reference={item}
              onOpen={openEvidence}
              opening={openingId === (item.id || item.fileUrl || item.url)}
            />
          ))}
        </div>
      ) : (
        <p className="text-sm text-[#5A5A5A]">No evidence uploaded.</p>
      )}
    </div>
  );
};

export default EvidenceButton;
